import { useEffect, useRef, useState, type ComponentType } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userLogout } from '@/api/service/auth';
import { useAppContext } from '@/contexts/AppContext';
import { LogoutLoader } from './LogoutLoader';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { cn } from './ui/utils';
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Users,
  Wallet,
} from 'lucide-react';

const LOGO_URL =
  'https://media.istockphoto.com/id/1423550966/vector/profit-rounded-lines-icon.jpg?s=612x612&w=0&k=20&c=_KFEK2PUIlquKGVUYQ18I2rO6xQ3ieFDEx-xHpXRLTI=';

/** Remembers collapsed/expanded across reloads. Cleared on login and logout so each session starts expanded. */
export const SIDEBAR_COLLAPSED_KEY = 'pms-sidebar-collapsed';

function readCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}

function saveCollapsed(value: boolean) {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(value));
  } catch {
    // Storage unavailable (e.g. private mode); the sidebar still works for this page view.
  }
}

const ADMIN_NAME = 'Perumal B';
const ADMIN_ROLE = 'Admin';
const TAMIL_NAME = 'பொருளாதார முன்னேற்ற சங்கம்';

/** Time the contents take to fade out before the width changes. */
const FADE_OUT_MS = 140;

const menuItems = [
  { id: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'members', path: '/members', label: 'Members', icon: Users },
  { id: 'payments', path: '/payments', label: 'Payments', icon: CreditCard },
  { id: 'loans', path: '/loans', label: 'Loans', icon: Wallet },
  { id: 'balances', path: '/balances', label: 'Balances', icon: Coins },
] as const;

/*
 * Layout note: the collapsed sidebar is 92px wide with 10px side padding, so every
 * icon, the logo and the avatar are centred 46px from the left edge. The expanded
 * paddings are picked so they stay at 46px too, which keeps icons still while
 * the sidebar expands or collapses.
 */

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function Logo({ size }: { size: 'sm' | 'md' }) {
  return (
    <img
      src={LOGO_URL}
      alt="PMS Logo"
      className={cn(
        'shrink-0 rounded-full border-[3px] border-white object-cover ring-1 ring-[#d5e0ef]',
        size === 'md' ? 'h-[42px] w-[42px]' : 'h-[38px] w-[38px]'
      )}
    />
  );
}

function SidebarHeader({ collapsed }: { collapsed: boolean }) {
  if (collapsed) {
    return (
      <>
        <div className="flex w-full flex-col items-center gap-2 rounded-[14px] border border-[#e6eaf1] bg-white px-2.5 pb-2.5 pt-3 shadow-[0_1px_3px_rgba(20,33,61,0.06)]">
          <Logo size="sm" />
          <span className="text-xs font-semibold leading-none tracking-[0.14em] text-gray-900">
            PMS
          </span>
        </div>
        <div className="mx-1.5 mt-4 h-px bg-[#e6eaf1]" />
      </>
    );
  }

  return (
    <div className="w-full rounded-2xl border border-[#e6eaf1] bg-[#f4f7fc] p-2.5">
      <div className="flex items-center gap-3">
        <Logo size="md" />
        <div className="min-w-0 flex-1">
          <p className="text-lg font-semibold leading-tight tracking-[0.14em] text-gray-900">PMS</p>
          <p className="mt-[5px] text-[10px] font-semibold uppercase leading-none tracking-[0.18em] text-gray-500">
            Admin Portal
          </p>
        </div>
      </div>
      <p className="mt-2.5 whitespace-nowrap border-t border-dashed border-[#d5e0ef] pt-2.5 text-center text-[11px] font-medium text-[#2f4f7f]">
        {TAMIL_NAME}
      </p>
    </div>
  );
}

function NavItem({
  collapsed,
  isActive,
  label,
  icon: Icon,
  onClick,
}: {
  collapsed: boolean;
  isActive: boolean;
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex w-full cursor-pointer items-center rounded-[14px] transition-colors',
        collapsed
          ? 'h-[68px] flex-col justify-center gap-[5px] text-[11px]'
          : 'h-[52px] gap-3.5 px-[15px] text-sm',
        isActive
          ? 'bg-[#eaf0f8] font-semibold text-[#2f4f7f]'
          : 'font-medium text-gray-700 hover:bg-gray-50'
      )}
    >
      <span
        className={cn(
          'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full transition-colors',
          isActive ? 'bg-[#4369a0] text-white' : 'bg-[#f1f4f9] text-[#5b6475]'
        )}
      >
        <Icon className="h-[17px] w-[17px]" />
      </span>
      <span className="truncate">{label}</span>
    </button>
  );
}

function LogoutButton({
  collapsed,
  disabled,
  onClick,
}: {
  collapsed: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label="Logout"
          className={cn(
            'flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-[10px] text-gray-500 transition-colors hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50',
            collapsed ? 'bg-[#f4f6fa] hover:bg-[#eaf0f8]' : 'bg-white hover:bg-[#eaf0f8]'
          )}
        >
          <LogOut className="h-[17px] w-[17px]" />
        </button>
      </TooltipTrigger>
      <TooltipContent side={collapsed ? 'right' : 'top'} sideOffset={8}>
        Logout
      </TooltipContent>
    </Tooltip>
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated } = useAppContext();
  // Read on first render so a reload shows the saved mode straight away, without animating.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [isFading, setIsFading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Clear the timer if the sidebar unmounts mid-fade.
  useEffect(() => () => clearTimeout(fadeTimer.current), []);

  // Fade the contents out, then switch mode; the width slides and contents fade back in.
  const toggleCollapsed = () => {
    if (isFading) return;
    setIsFading(true);
    fadeTimer.current = setTimeout(() => {
      setCollapsed((prev) => {
        const next = !prev;
        saveCollapsed(next);
        return next;
      });
      setIsFading(false);
    }, FADE_OUT_MS);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await userLogout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setTimeout(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem(SIDEBAR_COLLAPSED_KEY);
        setIsAuthenticated(false);
        setIsLoggingOut(false);
        navigate('/login', { replace: true });
      }, 1000);
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex h-screen shrink-0 overflow-hidden border-r border-[#e6eaf1] bg-white shadow-[1px_0_12px_rgba(20,33,61,0.04)]',
          'transition-[width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none',
          collapsed ? 'w-[92px]' : 'w-60'
        )}
      >
        <div
          className={cn(
            'flex h-full w-full flex-col py-[18px] transition-opacity motion-reduce:transition-none',
            collapsed ? 'px-2.5' : 'px-3.5',
            isFading ? 'opacity-0 duration-150' : 'opacity-100 delay-100 duration-300'
          )}
        >
          <SidebarHeader collapsed={collapsed} />

          {/* Navigation */}
          <nav className="mt-[22px] flex flex-1 flex-col gap-2 overflow-y-auto scrollbar-hide">
            {menuItems.map((item) => (
              <NavItem
                key={item.id}
                collapsed={collapsed}
                isActive={location.pathname === item.path}
                label={item.label}
                icon={item.icon}
                onClick={() => navigate(item.path)}
              />
            ))}
          </nav>

          {/* Collapse toggle */}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'mb-2.5 flex h-[38px] w-full shrink-0 cursor-pointer items-center gap-2 rounded-[10px] border border-dashed border-[#e6eaf1] text-[13px] font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700',
              collapsed ? 'justify-center' : 'justify-start pl-6'
            )}
          >
            {collapsed ? (
              <ChevronRight className="h-[15px] w-[15px]" />
            ) : (
              <>
                <ChevronLeft className="h-[15px] w-[15px]" />
                <span>Collapse</span>
              </>
            )}
          </button>

          {/* Admin footer */}
          <div
            className={cn(
              'flex shrink-0 items-center gap-2.5 rounded-[14px]',
              collapsed
                ? 'flex-col border border-[#e6eaf1] bg-white p-2.5 shadow-[0_1px_3px_rgba(20,33,61,0.06)]'
                : 'bg-[#f4f6fa] px-3.5 py-2.5'
            )}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eaf0f8] text-[13px] font-bold text-[#2f4f7f]">
                  {getInitials(ADMIN_NAME)}
                </div>
              </TooltipTrigger>
              {collapsed && (
                <TooltipContent side="right" sideOffset={8}>
                  <p className="font-medium">{ADMIN_NAME}</p>
                  <p className="text-primary-foreground/80">{ADMIN_ROLE}</p>
                </TooltipContent>
              )}
            </Tooltip>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900">{ADMIN_NAME}</p>
                <p className="text-xs text-gray-500">{ADMIN_ROLE}</p>
              </div>
            )}
            <LogoutButton collapsed={collapsed} disabled={isLoggingOut} onClick={handleLogout} />
          </div>
        </div>
      </aside>
      {isLoggingOut && <LogoutLoader />}
    </TooltipProvider>
  );
}
